import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Calendar, MapPin, Video, Clock, Award, Users, ArrowRight, MessageSquare, Heart, Pin,
  Search, Send, Smile, Paperclip, CheckCheck, Newspaper, ExternalLink, Briefcase, Building2,
  ShieldCheck, Star, Gift, Copy, TrendingUp, BarChart3, Target, Sparkles, ChevronRight, Plus, Filter,
  Loader2, X, Eye, EyeOff,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  MOCK_EVENTI, MOCK_FORUM_CATS, MOCK_MESSAGGI, MOCK_CHAT_ATTIVA,
  MOCK_NEWS, MOCK_CONVENZIONI, MOCK_STATS,
} from "../data/extraMock";

const API = process.env.REACT_APP_BACKEND_URL;

function timeAgo(iso) {
  if (!iso) return "";
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "ora";
  if (m < 60) return `${m}m fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h fa`;
  return `${Math.floor(h / 24)}g fa`;
}

// ============ EVENTI & CALENDARIO ============
export function EventiTab() {
  const [view, setView] = useState("lista");
  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-extrabold text-[#0a1f55]">Eventi & Calendario</h2>
          <p className="text-[13.5px] text-slate-500 mt-1">Masterclass live, workshop e convegni A.C.D.T.</p>
        </div>
        <div className="flex bg-white border border-slate-200 rounded-lg p-1">
          {["lista", "calendario"].map((v) => (
            <button key={v} data-testid={`eventi-view-${v}`} onClick={() => setView(v)}
              className={`px-3 py-1.5 text-[12.5px] font-bold rounded-md capitalize ${view === v ? "bg-[#102a6b] text-white" : "text-slate-600"}`}>
              {v}
            </button>
          ))}
        </div>
      </div>

      {view === "lista" ? (
        <div className="grid md:grid-cols-2 gap-5">
          {MOCK_EVENTI.map((e) => (
            <div key={e.id} className="rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-[#102a6b]/40 hover:shadow-lg transition-all">
              <div className="aspect-[16/8] relative overflow-hidden bg-slate-100">
                <img src={e.immagine} alt={e.titolo} className="w-full h-full object-cover" />
                <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 text-[10.5px] font-extrabold uppercase tracking-wider text-[#0a1f55]">
                  {e.tipo}
                </div>
                {e.iscritto && (
                  <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500 text-white text-[10.5px] font-extrabold uppercase tracking-wider">
                    ✓ Iscritto
                  </div>
                )}
                <div className="absolute bottom-3 left-3 bg-[#0a1f55] text-white rounded-lg p-2 text-center min-w-[54px]">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[#fbbf24]">{new Date(e.data).toLocaleDateString("it-IT", { month: "short" })}</div>
                  <div className="text-xl font-extrabold leading-none">{new Date(e.data).getDate()}</div>
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-extrabold text-[#0a1f55] leading-tight line-clamp-2 min-h-[2.7rem]">{e.titolo}</h3>
                <div className="mt-3 flex flex-wrap gap-2 text-[12px] text-slate-600">
                  <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {e.ora}</span>
                  {e.modalita === "in presenza" ? (
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {e.luogo}</span>
                  ) : e.modalita === "online" ? (
                    <span className="inline-flex items-center gap-1"><Video className="h-3.5 w-3.5" /> Live online</span>
                  ) : (
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {e.luogo} + online</span>
                  )}
                  {e.cfdt > 0 && <span className="inline-flex items-center gap-1 font-bold text-[#b45309]"><Award className="h-3.5 w-3.5" /> +{e.cfdt} CFDT</span>}
                </div>
                <div className="mt-3 text-[12.5px] text-slate-500 line-clamp-2">{e.descrizione}</div>
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
                  <div className="text-[11px] text-slate-500">
                    <Users className="inline h-3.5 w-3.5 mr-1" />
                    <strong className="text-[#102a6b]">{e.iscritti}</strong> / {e.posti} posti
                  </div>
                  <Button className={`font-bold ${e.iscritto ? "bg-slate-100 text-slate-700 hover:bg-slate-200" : "bg-[#102a6b] hover:bg-[#0a1f55] text-white"}`}>
                    {e.iscritto ? "Già iscritto" : "Iscriviti"} <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <CalendarioView />
      )}
    </>
  );
}

function CalendarioView() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const days = [];
  for (let i = 0; i < startOffset; i++) days.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);

  const eventByDay = {};
  MOCK_EVENTI.forEach((e) => {
    const d = new Date(e.data);
    if (d.getMonth() === month && d.getFullYear() === year) {
      eventByDay[d.getDate()] = eventByDay[d.getDate()] || [];
      eventByDay[d.getDate()].push(e);
    }
  });

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-extrabold text-[#0a1f55] text-lg capitalize">{now.toLocaleDateString("it-IT", { month: "long", year: "numeric" })}</h3>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10.5px] uppercase tracking-wider font-bold text-slate-500 mb-2">
        {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map(d => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          const hasEvent = d && eventByDay[d];
          const isToday = d === now.getDate();
          return (
            <div key={i} className={`aspect-square rounded-lg p-1 text-[12px] ${!d ? "" : isToday ? "bg-[#f59e0b]/20 border-2 border-[#f59e0b]" : hasEvent ? "bg-[#102a6b]/8 border border-[#102a6b]/30 cursor-pointer hover:bg-[#102a6b]/15" : "border border-slate-100 hover:bg-slate-50"}`}>
              {d && (
                <>
                  <div className={`font-bold ${isToday ? "text-[#b45309]" : hasEvent ? "text-[#102a6b]" : "text-slate-600"}`}>{d}</div>
                  {hasEvent && (
                    <div className="mt-0.5 space-y-0.5">
                      {eventByDay[d].slice(0, 1).map(e => (
                        <div key={e.id} className="text-[9px] font-bold text-[#102a6b] truncate bg-white rounded px-1 py-0.5">{e.titolo}</div>
                      ))}
                      {eventByDay[d].length > 1 && <div className="text-[9px] text-[#102a6b]">+{eventByDay[d].length - 1}</div>}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============ FORUM ============
export function ForumTab() {
  const [cat, setCat] = useState("tutti");
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ titolo: "", contenuto: "", categoria: "domande" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = async (catFilter) => {
    setLoading(true);
    try {
      const url = catFilter && catFilter !== "tutti"
        ? `${API}/api/forum/topics?categoria=${catFilter}`
        : `${API}/api/forum/topics`;
      const { data } = await axios.get(url);
      setTopics(data);
    } catch (e) {
      setTopics([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(cat); }, [cat]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await axios.post(`${API}/api/forum/topics`, form, { withCredentials: true });
      setNewOpen(false);
      setForm({ titolo: "", contenuto: "", categoria: "domande" });
      await load(cat);
    } catch (e2) {
      setError(e2.response?.data?.detail || "Errore creazione topic");
    } finally {
      setSaving(false);
    }
  };

  const handleLike = async (id) => {
    try {
      await axios.post(`${API}/api/forum/topics/${id}/like`, {}, { withCredentials: true });
      await load(cat);
    } catch (_e) { /* ignore */ }
  };

  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-extrabold text-[#0a1f55]">Forum della Community</h2>
          <p className="text-[13.5px] text-slate-500 mt-1">Discuti con gli altri soci A.C.D.T.</p>
        </div>
        <Button data-testid="btn-new-topic" onClick={() => setNewOpen(true)} className="bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold">
          <Plus className="mr-2 h-4 w-4" /> Nuovo Topic
        </Button>
      </div>

      <div className="grid md:grid-cols-4 gap-3">
        {MOCK_FORUM_CATS.map((c) => (
          <button key={c.id} data-testid={`forum-cat-${c.id}`} onClick={() => setCat(cat === c.id ? "tutti" : c.id)}
            className={`rounded-xl border p-4 text-left transition-all ${cat === c.id ? "border-[#102a6b] bg-[#102a6b]/5" : "border-slate-200 bg-white hover:border-[#102a6b]/40"}`}>
            <div className="h-2 w-8 rounded-full mb-3" style={{ background: c.color }} />
            <div className="font-extrabold text-[#0a1f55]">{c.label}</div>
            <div className="text-[11.5px] text-slate-500 mt-0.5">{topics.filter(t => t.categoria === c.id).length} discussioni</div>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#102a6b]" /></div>
      ) : topics.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <MessageSquare className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <h3 className="font-bold text-[#0a1f55]">Nessuna discussione ancora</h3>
          <p className="text-[13px] text-slate-500 mt-1">Sii il primo a creare un topic!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {topics.map((t) => {
            const catObj = MOCK_FORUM_CATS.find(x => x.id === t.categoria) || { color: "#94a3b8", label: t.categoria };
            return (
              <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4 flex gap-4 hover:border-[#102a6b]/40 hover:shadow-sm transition-all">
                <div className="h-11 w-11 rounded-full bg-gradient-to-br from-[#102a6b] to-[#1e40af] text-white flex items-center justify-center font-bold text-[12px] flex-shrink-0">
                  {t.autore_iniziali}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {t.pinned && <Pin className="h-3.5 w-3.5 text-[#f59e0b]" />}
                    <h3 className="font-extrabold text-[#0a1f55] text-[15px]">{t.titolo}</h3>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider" style={{ background: `${catObj.color}20`, color: catObj.color }}>{catObj.label}</span>
                  </div>
                  <p className="text-[13px] text-slate-600 line-clamp-2">{t.contenuto}</p>
                  <div className="mt-2 flex items-center gap-4 text-[11.5px] text-slate-500">
                    <span>di <strong className="text-[#0a1f55]">{t.autore_nome}</strong></span>
                    <span className="inline-flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" /> {t.risposte_count}</span>
                    <button data-testid={`like-${t.id}`} onClick={() => handleLike(t.id)} className="inline-flex items-center gap-1 hover:text-pink-600">
                      <Heart className="h-3.5 w-3.5" /> {t.like_count}
                    </button>
                    <span>• {timeAgo(t.created_at)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {newOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={handleCreate} className="bg-white rounded-2xl p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-extrabold text-[#0a1f55]">Nuovo Topic</h3>
              <button type="button" onClick={() => setNewOpen(false)} className="h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-[12px] font-semibold text-[#0a1f55]">Categoria</Label>
                <select value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} className="mt-1.5 w-full h-10 rounded-md border border-slate-300 px-3 text-[14px]">
                  {MOCK_FORUM_CATS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[12px] font-semibold text-[#0a1f55]">Titolo *</Label>
                <Input required value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-[12px] font-semibold text-[#0a1f55]">Contenuto *</Label>
                <Textarea required value={form.contenuto} onChange={(e) => setForm({ ...form, contenuto: e.target.value })} className="mt-1.5 min-h-[120px]" />
              </div>
              {error && <div className="rounded bg-red-50 border border-red-200 text-red-700 p-2 text-[12px]">{error}</div>}
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setNewOpen(false)}>Annulla</Button>
              <Button data-testid="btn-save-topic" type="submit" disabled={saving} className="bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pubblica"}
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

// ============ MESSAGGI 1-a-1 (polling) ============
export function MessaggiTab() {
  const [conversazioni, setConversazioni] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeName, setActiveName] = useState("");
  const [messaggi, setMessaggi] = useState([]);
  const [meId, setMeId] = useState(null);
  const [testo, setTesto] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const loadConvs = async () => {
    try {
      const [c, me] = await Promise.all([
        axios.get(`${API}/api/me/messaggi/conversazioni`, { withCredentials: true }),
        axios.get(`${API}/api/auth/me`, { withCredentials: true }),
      ]);
      setConversazioni(c.data);
      setMeId(me.data.id);
    } catch (_e) { /* ignore */ }
    setLoading(false);
  };

  const loadChat = async (otherId) => {
    try {
      const { data } = await axios.get(`${API}/api/me/messaggi/${otherId}`, { withCredentials: true });
      setMessaggi(data);
    } catch (_e) { /* ignore */ }
  };

  useEffect(() => { loadConvs(); }, []);

  // Polling ogni 5s su conversazione attiva
  useEffect(() => {
    if (!activeId) return;
    loadChat(activeId);
    const t = setInterval(() => { loadChat(activeId); loadConvs(); }, 5000);
    return () => clearInterval(t);
  }, [activeId]);

  const handleSelect = (c) => {
    setActiveId(c.other_id);
    setActiveName(c.nome);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!testo.trim() || !activeId) return;
    setSending(true);
    try {
      await axios.post(`${API}/api/me/messaggi`, { destinatario_id: activeId, testo }, { withCredentials: true });
      setTesto("");
      await loadChat(activeId);
      await loadConvs();
    } catch (_e) { /* ignore */ }
    setSending(false);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" style={{ height: "70vh" }}>
      <div className="grid md:grid-cols-3 h-full">
        {/* Lista */}
        <div className="border-r border-slate-200 flex flex-col">
          <div className="p-4 border-b border-slate-200">
            <h2 className="font-extrabold text-[#0a1f55]">Messaggi</h2>
            <p className="text-[11.5px] text-slate-500 mt-0.5">Aggiornamento automatico ogni 5s</p>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#102a6b]" /></div>
            ) : conversazioni.length === 0 ? (
              <div className="p-6 text-center text-[12.5px] text-slate-400">Nessuna conversazione.<br />Apri il profilo di un socio dalla Directory per iniziare.</div>
            ) : conversazioni.map((c) => (
              <button key={c.other_id} data-testid={`conv-${c.other_id}`} onClick={() => handleSelect(c)}
                className={`w-full px-4 py-3 flex items-center gap-3 border-b border-slate-100 text-left transition-colors ${activeId === c.other_id ? "bg-[#102a6b]/5" : "hover:bg-slate-50"}`}>
                <div className="h-11 w-11 rounded-full bg-gradient-to-br from-[#102a6b] to-[#1e40af] text-white flex items-center justify-center font-bold text-[13px]">
                  {c.iniziali}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="font-bold text-[13.5px] text-[#0a1f55] truncate">{c.nome}</div>
                    <div className="text-[10.5px] text-slate-400">{timeAgo(c.ultimo_at)}</div>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <div className="text-[12px] text-slate-500 truncate flex-1">{c.ultimo_messaggio}</div>
                    {c.non_letti > 0 && <span className="h-5 min-w-[20px] px-1.5 rounded-full bg-[#f59e0b] text-[#0a1020] text-[10.5px] font-extrabold flex items-center justify-center">{c.non_letti}</span>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Conversazione attiva */}
        <div className="md:col-span-2 flex flex-col bg-[#f5f7ff]/40">
          {!activeId ? (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center"><MessageSquare className="h-10 w-10 mx-auto mb-2 text-slate-300" /><div className="text-[13px]">Seleziona una conversazione</div></div>
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-slate-200 bg-white">
                <div className="font-extrabold text-[#0a1f55]">{activeName}</div>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3">
                {messaggi.map((m) => {
                  const mine = m.mittente_id === meId;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${mine ? "bg-[#102a6b] text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-800 rounded-bl-md"}`}>
                        <div className="text-[13.5px]">{m.testo}</div>
                        <div className={`text-[10px] mt-1 flex items-center gap-1 ${mine ? "text-slate-300 justify-end" : "text-slate-400"}`}>
                          {timeAgo(m.created_at)}
                          {mine && m.letto && <CheckCheck className="h-3 w-3" />}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form onSubmit={handleSend} className="p-4 border-t border-slate-200 bg-white flex items-center gap-2">
                <Input data-testid="msg-input" value={testo} onChange={(e) => setTesto(e.target.value)} placeholder="Scrivi un messaggio..." className="flex-1 h-10" />
                <Button data-testid="msg-send" type="submit" disabled={sending || !testo.trim()} className="h-10 bg-[#102a6b] hover:bg-[#0a1f55] text-white">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ NEWS ============
export function NewsTab() {
  const pinned = MOCK_NEWS.find(n => n.pinned);
  const rest = MOCK_NEWS.filter(n => !n.pinned);
  return (
    <>
      <div>
        <h2 className="text-2xl font-extrabold text-[#0a1f55]">News & Comunicazioni</h2>
        <p className="text-[13.5px] text-slate-500 mt-1">Aggiornamenti ufficiali dal direttivo A.C.D.T.</p>
      </div>

      {pinned && (
        <div className="rounded-2xl border-2 border-[#f59e0b]/40 bg-white overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            <div className="aspect-video md:aspect-auto relative overflow-hidden bg-slate-100">
              <img src={pinned.immagine} alt={pinned.titolo} className="w-full h-full object-cover" />
              <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#f59e0b] text-[#0a1020] text-[10.5px] font-extrabold uppercase tracking-wider">
                <Pin className="h-3 w-3" /> In evidenza
              </div>
            </div>
            <div className="p-6 md:p-8 flex flex-col justify-center">
              <div className="text-[11px] uppercase tracking-wider font-bold text-[#b45309]">{pinned.categoria}</div>
              <h3 className="mt-2 text-xl md:text-2xl font-extrabold text-[#0a1f55] leading-tight">{pinned.titolo}</h3>
              <p className="mt-3 text-[13.5px] text-slate-600">{pinned.estratto}</p>
              <div className="mt-4 flex items-center gap-3 text-[11.5px] text-slate-500">
                <span><strong className="text-[#102a6b]">{pinned.autore}</strong></span>
                <span>•</span>
                <span>{new Date(pinned.data).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</span>
              </div>
              <Button className="mt-5 self-start bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold">
                Leggi articolo <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        {rest.map((n) => (
          <div key={n.id} className="rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-[#102a6b]/40 hover:shadow-md transition-all cursor-pointer">
            <div className="aspect-[16/9] overflow-hidden bg-slate-100">
              <img src={n.immagine} alt={n.titolo} className="w-full h-full object-cover" />
            </div>
            <div className="p-5">
              <div className="text-[10.5px] uppercase tracking-wider font-bold text-[#b45309]">{n.categoria}</div>
              <h3 className="mt-1 font-extrabold text-[#0a1f55] leading-tight">{n.titolo}</h3>
              <p className="mt-2 text-[12.5px] text-slate-600 line-clamp-2">{n.estratto}</p>
              <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-500">
                <Newspaper className="h-3.5 w-3.5" />
                <span>{n.autore}</span>
                <span>•</span>
                <span>{new Date(n.data).toLocaleDateString("it-IT")}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ============ DIRECTORY SOCI ============
export function DirectoryTab() {
  const [filter, setFilter] = useState("");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [savingOpt, setSavingOpt] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [dirRes, meRes] = await Promise.all([
        axios.get(`${API}/api/directory`),
        axios.get(`${API}/api/auth/me`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setEntries(dirRes.data);
      setMe(meRes.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleToggleOptIn = async () => {
    setSavingOpt(true);
    try {
      await axios.post(`${API}/api/me/directory/opt-in`, {
        visible: !me?.directory_visible,
        citta: me?.citta,
        provincia: me?.provincia,
        specializzazione: me?.specializzazione,
      }, { withCredentials: true });
      await load();
    } finally {
      setSavingOpt(false);
    }
  };

  const filtered = entries.filter(d => {
    const q = filter.toLowerCase();
    return !q || [d.nome, d.cognome, d.citta, d.provincia, d.specializzazione]
      .some(v => (v || "").toLowerCase().includes(q));
  });

  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-extrabold text-[#0a1f55]">Directory Soci</h2>
          <p className="text-[13.5px] text-slate-500 mt-1">Trova consulenti A.C.D.T. per specializzazione e città</p>
        </div>
        {me && (
          <Button
            data-testid="toggle-directory-opt"
            onClick={handleToggleOptIn}
            disabled={savingOpt}
            className={me.directory_visible ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-[#102a6b] hover:bg-[#0a1f55] text-white"}
          >
            {savingOpt ? <Loader2 className="h-4 w-4 animate-spin" /> :
              me.directory_visible ? <><Eye className="mr-2 h-4 w-4" /> Sei visibile</> :
              <><EyeOff className="mr-2 h-4 w-4" /> Mostrami nella directory</>}
          </Button>
        )}
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Cerca per nome, specializzazione o città..." value={filter} onChange={(e) => setFilter(e.target.value)} className="pl-10 h-11" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#102a6b]" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <Users className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <h3 className="font-bold text-[#0a1f55]">Nessun socio nella directory</h3>
          <p className="text-[13px] text-slate-500 mt-1">Sii il primo ad apparire: clicca &quot;Mostrami nella directory&quot;</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((d) => (
            <div key={d.id} data-testid={`directory-${d.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#102a6b]/40 hover:shadow-md transition-all">
              <div className="flex items-start gap-3">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#102a6b] to-[#1e40af] text-white flex items-center justify-center font-extrabold">{d.iniziali}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-extrabold text-[#0a1f55] text-[15px]">{d.nome} {d.cognome}</h3>
                    {d.verificato && <ShieldCheck className="h-4 w-4 text-emerald-600" />}
                  </div>
                  {(d.citta || d.provincia) && (
                    <div className="text-[11.5px] text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" /> {d.citta}{d.provincia ? ` (${d.provincia})` : ""}</div>
                  )}
                  {d.registro && (
                    <span className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#f59e0b]/15 text-[#b45309] text-[10px] font-extrabold uppercase tracking-wider">
                      <Award className="h-3 w-3" /> Registro
                    </span>
                  )}
                </div>
              </div>
              {d.specializzazione && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-bold">Specializzazione</div>
                  <div className="text-[13px] font-semibold text-[#102a6b] mt-0.5">{d.specializzazione}</div>
                  {d.anni && <div className="text-[11px] text-slate-500 mt-2">{d.anni} ann{d.anni === 1 ? "o" : "i"} di esperienza</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl bg-[#102a6b]/5 border border-[#102a6b]/15 p-4 text-[12.5px] text-[#102a6b]">
        💡 La tua presenza nella directory è <strong>opt-in</strong>: solo se attivi il pulsante &quot;Mostrami nella directory&quot;.
      </div>
    </>
  );
}

// ============ CONVENZIONI ============
export function ConvenzioniTab() {
  return (
    <>
      <div className="rounded-2xl bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] text-white p-6 md:p-8 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 h-44 w-44 rounded-full bg-[#f59e0b]/25 blur-3xl" />
        <div className="relative max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f59e0b] text-[#0a1f55] text-[11px] font-extrabold uppercase tracking-wider">
            <Gift className="h-3 w-3" /> Esclusivo Soci
          </div>
          <h2 className="mt-3 text-2xl md:text-3xl font-extrabold">Convenzioni & Benefit</h2>
          <p className="mt-2 text-slate-200 text-[14px]">Sconti riservati ai soci A.C.D.T. su software, formazione, servizi legali e assicurazioni.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {MOCK_CONVENZIONI.map((c) => (
          <div key={c.id} data-testid={`conv-${c.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#102a6b]/40 hover:shadow-md transition-all">
            <div className="flex items-start justify-between gap-3">
              <div className="h-14 w-14 rounded-2xl flex items-center justify-center font-extrabold text-white text-lg" style={{ background: c.colore }}>
                {c.brand[0]}
              </div>
              <div className="text-right">
                <div className="text-3xl font-extrabold text-[#0a1f55] leading-none">-{c.sconto}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-1">Sconto</div>
              </div>
            </div>
            <h3 className="mt-4 font-extrabold text-[#0a1f55] text-[16px]">{c.brand}</h3>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mt-0.5">{c.categoria}</div>
            <p className="text-[12.5px] text-slate-600 mt-2 line-clamp-2 min-h-[2.5rem]">{c.desc}</p>
            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-2">
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Codice</div>
                <div className="font-mono font-extrabold text-[#0a1f55] text-[13px]">{c.codice}</div>
              </div>
              <Button className="bg-[#102a6b] hover:bg-[#0a1f55] text-white text-[12px] h-9"><Copy className="mr-1.5 h-3.5 w-3.5" /> Copia</Button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ============ STATISTICHE ============
export function StatistichePersonaliTab() {
  const s = MOCK_STATS;
  const maxOre = Math.max(...s.oreStudioMese.map(m => m.v));
  const maxCfdt = Math.max(...s.cfdtMese.map(m => m.v));

  return (
    <>
      <div>
        <h2 className="text-2xl font-extrabold text-[#0a1f55]">Statistiche personali</h2>
        <p className="text-[13.5px] text-slate-500 mt-1">Monitora il tuo percorso formativo e confrontati con la community</p>
      </div>

      {/* KPI */}
      <div className="grid md:grid-cols-3 gap-4">
        <StatCard icon={Clock} color="#3b82f6" label="Ore di studio" value={s.totali.oreStudio} sub={`Media soci: ${s.mediaSoci.oreStudio}h`} delta="+18 vs media" />
        <StatCard icon={Award} color="#f59e0b" label="CFDT totali" value={s.totali.cfdtTotali} sub={`Media soci: ${s.mediaSoci.cfdtTotali}`} delta="+3 vs media" />
        <StatCard icon={Users} color="#22c55e" label="Networking" value={s.totali.networking} sub="contatti attivi" />
      </div>

      {/* Grafico ore */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-extrabold text-[#0a1f55]">Ore di studio per mese</h3>
            <p className="text-[12px] text-slate-500">Ultimi 7 mesi</p>
          </div>
          <BarChart3 className="h-5 w-5 text-slate-400" />
        </div>
        <div className="flex items-end gap-3 h-48">
          {s.oreStudioMese.map((m) => {
            const h = (m.v / maxOre) * 100;
            return (
              <div key={m.m} className="flex-1 h-full flex flex-col items-center group">
                <div className="text-[11px] font-bold text-[#102a6b] mb-1 group-hover:opacity-100 opacity-0 transition-opacity">{m.v}h</div>
                <div className="w-full flex-1 flex items-end">
                  <div className="w-full rounded-t-lg bg-gradient-to-t from-[#102a6b] to-[#3b82f6] transition-all hover:from-[#0a1f55] hover:to-[#1e40af]" style={{ height: `${h}%`, minHeight: "4px" }} />
                </div>
                <div className="text-[11px] text-slate-500 font-semibold mt-2">{m.m}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Grafico CFDT */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-extrabold text-[#0a1f55]">CFDT accumulati per mese</h3>
            <p className="text-[12px] text-slate-500">Andamento credito formativo</p>
          </div>
          <TrendingUp className="h-5 w-5 text-slate-400" />
        </div>
        <div className="flex items-end gap-3 h-48">
          {s.cfdtMese.map((m) => {
            const h = (m.v / maxCfdt) * 100;
            return (
              <div key={m.m} className="flex-1 h-full flex flex-col items-center group">
                <div className="text-[11px] font-bold text-[#b45309] mb-1 group-hover:opacity-100 opacity-0 transition-opacity">{m.v}</div>
                <div className="w-full flex-1 flex items-end">
                  <div className="w-full rounded-t-lg bg-gradient-to-t from-[#d97706] to-[#fbbf24] transition-all" style={{ height: `${m.v === 0 ? 2 : h}%`, minHeight: "4px" }} />
                </div>
                <div className="text-[11px] text-slate-500 font-semibold mt-2">{m.m}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Confronto */}
      <div className="rounded-2xl bg-gradient-to-br from-[#102a6b]/5 to-white border border-[#102a6b]/15 p-6">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#102a6b] to-[#1e40af] text-white flex items-center justify-center">
            <Target className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-extrabold text-[#0a1f55]">Sei al di sopra della media!</h3>
            <p className="text-[13px] text-slate-600 mt-1">
              Hai studiato <strong className="text-[#102a6b]">{s.totali.oreStudio - s.mediaSoci.oreStudio} ore in più</strong> rispetto alla media dei soci A.C.D.T.
              Continua così per mantenere il tuo livello professionale al top.
            </p>
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#f59e0b]/15 text-[#b45309] text-[12px] font-extrabold">
              <Sparkles className="h-3.5 w-3.5" /> Top 18% dei soci
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function StatCard({ icon: Icon, color, label, value, sub, delta }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: `${color}20`, color }}>
          <Icon className="h-5 w-5" />
        </div>
        {delta && <span className="text-[11px] font-extrabold text-emerald-600">{delta}</span>}
      </div>
      <div className="mt-3 text-[11.5px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-3xl font-extrabold text-[#0a1f55]">{value}</div>
      <div className="text-[12px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}
